import { useState, useRef } from 'preact/hooks';
import { cn } from '../lib/cn';
import {
  SendIcon,
  PaperclipIcon,
  CloseIcon,
  ImageFileIcon,
  DocumentFileIcon,
} from './icons';
import { formatFileSize } from '../client/media';
import { t } from '../lib/i18n';
import type { AttachmentsConfig } from '../client/api-types';
import type { PendingAttachment } from './useComposerAttachment';

interface MessageInputProps {
  onSend: (text: string, attachment?: PendingAttachment['uploaded']) => void;
  brandingColor: string;
  /** Override for the input placeholder. Falls back to the i18n default. */
  placeholder?: string;
  /**
   * Visitor attachment policy from the handshake. **Absent = no attach
   * button.** The server enforces the same policy on its own, so this only
   * decides whether to offer the affordance.
   */
  attachments?: AttachmentsConfig;
  /** File the visitor is about to send, owned by the panel. */
  pending?: PendingAttachment | null;
  /** Hands a picked or pasted file to the panel's attachment state. */
  onPickFile?: (file: File) => void;
  onClearAttachment?: () => void;
}

/**
 * What the file picker will accept, derived from the widget's own policy.
 *
 * Documents use extensions rather than mime types on purpose: the docx mime
 * alone is 73 characters, and this bundle ships on customers' pages under a
 * hard gzip budget. Browsers match extensions just as well here, and the
 * server validates the real mime anyway.
 *
 * SVG is left out of the image list — the server refuses it, so offering it
 * in the picker would only produce a rejection the visitor cannot act on.
 */
const ACCEPT_BY_KIND: Record<string, string> = {
  image: 'image/png,image/jpeg,image/webp,image/gif',
  document: '.pdf,.txt,.doc,.docx',
};

export function MessageInput({
  onSend,
  brandingColor,
  placeholder,
  attachments,
  pending,
  onPickFile,
  onClearAttachment,
}: MessageInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const canAttach = !!attachments && !!onPickFile;
  // Both icons are already in the bundle — MessageAttachment uses them — so
  // showing one here costs nothing but the markup.
  const ChipIcon = pending?.kind === 'image' ? ImageFileIcon : DocumentFileIcon;
  const readableSize = pending ? formatFileSize(pending.size) : '';
  const accept = (attachments?.allowedKinds ?? [])
    .map((kind) => ACCEPT_BY_KIND[kind] ?? '')
    .filter(Boolean)
    .join(',');

  // Nothing to send while an upload is still in flight or failed — the
  // receipt is what the server needs, and there is none yet.
  const hasReady = !!pending?.uploaded;
  const isBlocked = !!pending && !hasReady;
  const isEmpty = value.trim().length === 0 && !hasReady;

  function clearAttachment() {
    onClearAttachment?.();
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) onPickFile?.(file);
  }

  /**
   * Screenshot straight into the chat. Only fires when the clipboard carries
   * a file — a normal text paste must go through untouched.
   */
  function handlePaste(e: ClipboardEvent) {
    if (!canAttach) return;

    const file = e.clipboardData?.files?.[0];
    if (!file) return;

    e.preventDefault();
    onPickFile?.(file);
  }

  function submit() {
    if (isEmpty || isBlocked) return;
    onSend(value.trim(), pending?.uploaded);
    setValue('');
    clearAttachment();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function handleInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    setValue(target.value);
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
  }

  return (
    <div
      data-testid="composer"
      class={cn(
        'relative bg-w-composer border-t border-w-border',
        'transition-[background] duration-[350ms]',
      )}
    >
      {pending && (
        <div class="px-3 pt-3">
          <div
            data-testid="chip"
            class={cn(
              'flex items-center gap-2 px-[10px] py-[7px] rounded-[12px]',
              // Theme tokens, not a fixed black overlay: `bg-black/[0.05]`
              // vanishes on the dark composer and leaves the filename
              // floating with no container. `w-input` is lighter than the
              // composer in BOTH themes, which is exactly what a chip needs.
              // The foreground has to be explicit too: without it the chip
              // inherits a dark colour from an ancestor and the filename is
              // unreadable on the dark surface. `w-bb-fg` flips per theme
              // (#242D33 light / #E4EAEE dark).
              'bg-w-input border border-w-border',
              'text-[13px] text-w-bb-fg',
            )}
          >
            <ChipIcon class="w-[18px] h-[18px] shrink-0 opacity-60" />
            <span class="flex flex-col min-w-0 flex-1">
              <span class="truncate leading-[1.3]">{pending.name}</span>
              {!!readableSize && (
                <span class="text-[11px] text-w-secondary leading-[1.3]">
                  {readableSize}
                </span>
              )}
            </span>
            {pending.uploading && (
              <span class="text-[11px] text-w-secondary shrink-0">
                {t('uploading_file')}
              </span>
            )}
            <button
              type="button"
              onClick={clearAttachment}
              aria-label={t('remove_attachment')}
              class="shrink-0 p-1 rounded-full hover:bg-black/[0.06] text-w-secondary"
            >
              <CloseIcon class="w-3 h-3" />
            </button>
          </div>
          {pending.error && (
            <p role="alert" class="mt-1 px-1 text-[12px] text-red-600">
              {pending.error}
            </p>
          )}
        </div>
      )}

      <div class="flex items-end gap-2 px-3 pt-3 pb-3">
        {canAttach && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              onChange={handleFile}
              class="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label={t('attach_file')}
              class={cn(
                'flex items-center justify-center shrink-0',
                'w-[40px] h-[40px] rounded-full',
                'border-0 bg-transparent cursor-pointer outline-none',
                'text-w-secondary hover:bg-black/[0.05] transition-colors',
              )}
            >
              <PaperclipIcon class="w-5 h-5" />
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder ?? t('input_placeholder')}
          rows={1}
          aria-label={t('input_aria')}
          class={cn(
            'flex-1 resize-none rounded-[24px] px-4 py-2.5',
            'text-[15px] text-w-bb-fg leading-[1.4]',
            'bg-w-input border border-transparent',
            'placeholder:text-w-secondary/60',
            'outline-none focus:border-w-border',
            'transition-colors duration-150',
            'min-h-[40px] max-h-[120px]',
            'overflow-y-auto',
          )}
        />
        <button
          type="button"
          onClick={submit}
          disabled={isEmpty || isBlocked}
          aria-label={t('send_message')}
          style={{ '--mirlo-brand': brandingColor } as Record<string, string>}
          class={cn(
            'flex items-center justify-center',
            'w-[44px] h-[44px] rounded-full flex-shrink-0',
            'border-0 outline-none',
            'focus-visible:ring-2 focus-visible:ring-offset-1',
            isEmpty || isBlocked
              ? 'bg-w-input text-w-secondary/40 cursor-not-allowed scale-[0.88] opacity-55'
              : 'bg-[var(--mirlo-brand)] text-white hover:opacity-90 scale-100 opacity-100 cursor-pointer',
            'transition-all duration-[180ms] ease-[cubic-bezier(.34,1.56,.64,1)]',
          )}
        >
          <SendIcon class="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
